/**
 * This file defines the Program component, which serves as a container for displaying programming activity details and related issues.
 * The component renders the ProgrammingDetails component and the IssuesStudent component within tabs. Note that IssuesStudent is 
 * a placeholder component.
 */
import React, { useState } from "react";
import { useSelector } from "react-redux";
import ProgrammingDetails from "Screens1/Student/Course/Content/Programming/ComponentsNew/ProgrammingDetails/ProgrammingDetails.jsx";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import IssuesStudent from "Navigation/Course/Interactions/Issues/Student/issues.jsx";
import Countdown from "react-countdown";
import moment from "moment";
import countdownRenderer from "Components/Common/CountDownRenderer";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import CreateIssueModal from "Screens1/Course/Interactions/Issues/Student/CreateIssueModal.jsx";
import httpService from 'services/httpService.js';

function Program({
  programId,
  courseId,
  labID,
  labTitle,
  activity_title,
  activity_description,
  AccessControlsProgActivity,
  ProgrammingActivityDetails,
  setAccessControlsProgActivity,
  studentData,
  isExam,
  provideAdditionalFiles,
  instructorAdditionalFiles,
  document,
  displayRunWithoutSubmitButton,
  handleTimeUp,
}) {
  const userInfo = useSelector((state) => state.userInfo) || {};
  const userCourseRole = useSelector((state) => state.userCourseRole);
  const isInstructorUser = userInfo.mode === 'I' || userCourseRole === 'INSTRUCTOR' || userCourseRole === 'STA' || userCourseRole === 'JTA';
  const canStudentRaiseIssue = !isInstructorUser && (userCourseRole === 'STUDENT' || !userCourseRole);

  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueDefaultContext, setIssueDefaultContext] = useState(null);

  const changeAccess = (update) => {
    setAccessControlsProgActivity(update);
  };

  const handleOpenIssueModal = () => {
    const effectiveLabTitle = labTitle || `Lab #${labID}`;
    setIssueDefaultContext({
      category: 'LAB_ACTIVITY',
      labId: labID,
      labTitle: effectiveLabTitle,
      activityId: programId,
      activityName: activity_title,
      contextText: `Labs > ${effectiveLabTitle} > ${activity_title}`
    });
    setShowIssueModal(true);
  };

  const handleCreateIssueSubmit = async (newIssueData) => {
    const fd = new FormData();
    fd.append('course', courseId);
    fd.append('title', newIssueData.title);
    fd.append('description', newIssueData.description);
    fd.append('priority', newIssueData.priority);
    fd.append('category', newIssueData.category);
    fd.append('context', (newIssueData.category === 'LAB_ACTIVITY' || newIssueData.category === 'LAB') ? 'lab' : 'course');
    fd.append('context_text', newIssueData.context_text);
    if (newIssueData.labId) fd.append('lab_id', newIssueData.labId);
    if (newIssueData.activityId) fd.append('activity_id', newIssueData.activityId);
    if (newIssueData.conceptId) fd.append('concept_id', newIssueData.conceptId);
    if (newIssueData.mediaFile) fd.append('supporting_media', newIssueData.mediaFile);

    const rawToken = localStorage.getItem('token');
    const authHeaders = rawToken ? { Authorization: rawToken.startsWith('Token ') ? rawToken : `Token ${rawToken}` } : {};
    const res = await httpService.post('/issues/createIssue/', fd, { headers: authHeaders });
    return res.data?.issue || res.data;
  };

  const countdownKey = studentData?.end_time
    ? `cd-${new Date(studentData.end_time).getTime()}`
    : studentData?.start_time && studentData?.duration
      ? `cd-${new Date(studentData.start_time).getTime()}-${studentData.duration}`
      : 'cd-default';

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 px-1 pt-1">
        <h5 className="mb-0 font-weight-bold text-dark">{activity_title}</h5>
        {canStudentRaiseIssue && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleOpenIssueModal}
            className="d-flex justify-content-center align-items-center px-4 py-2 cursor-pointer rounded-pill border-0"
            style={{ fontWeight: "bold", whiteSpace: 'nowrap' }}
          >
            Raise an Issue
          </Button>
        )}
      </div>

      {/* Tabs for displaying programming activity details and related issues */}
      <Tabs defaultActiveKey="profile" id="uncontrolled-tab-example">
        <Tab eventKey="profile" title="Programming details">
          <div style={{ overflowX: "hidden" }}>
            <ProgrammingDetails
              programId={programId}
              activity_title={activity_title}
              activity_description={activity_description}
              details={ProgrammingActivityDetails}
              changeAccess={changeAccess}
              access={AccessControlsProgActivity}
              provideAdditionalFiles={provideAdditionalFiles}
              instructorAdditionalFiles={instructorAdditionalFiles}
              document={document}
              displayRunWithoutSubmitButton={displayRunWithoutSubmitButton}
            />
          </div>
        </Tab>
        <Tab eventKey="issues" title="Issues">
          <IssuesStudent />
        </Tab>
        <Tab
          title={
            isExam &&
              studentData &&
              studentData.start_time &&
              !studentData.is_finished ? (
              <>
                {
                  (studentData?.end_time) ? <Countdown
                    key={countdownKey}
                    date={
                      new Date(studentData.end_time).getTime()
                    }
                    onComplete={() => handleTimeUp()}
                    renderer={countdownRenderer}
                  />
                    : <Countdown
                      key={countdownKey}
                      date={
                        new Date(studentData.start_time).getTime() +
                        moment.duration(studentData.duration).asMilliseconds()
                      }
                      onComplete={() => handleTimeUp()}
                      renderer={countdownRenderer}
                    />
                }
              </>
            ) : null
          }
        ></Tab>
          <Tab
              title={
              AccessControlsProgActivity?.enable_show_codevaani && false ?
              (
                <>
                <Button
                    style={{ position:"relative", top: "-10px" }}
                  variant="primary">
                  <Link 
                  style={{ color: "white", textDecoration: "none" }}
                  to={`/courseware/course/${courseId}/content/labs/${labID}/programming/${programId}/codevaani`}>
                    Use Voicebot
                  </Link>
                </Button>
                </>
              ) : null
              }
          ></Tab>
      </Tabs>

      <CreateIssueModal
        show={showIssueModal}
        onHide={() => setShowIssueModal(false)}
        onSubmit={handleCreateIssueSubmit}
        defaultContext={issueDefaultContext}
      />
    </div>
  );
}

export default Program;
